import { useState, useEffect } from 'react';
import {
  collectionGroup, query, where, getDocs, getDoc, doc, onSnapshot,
  collection, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * useResolvedList — resolves a listId to its concrete list doc, even
 * when the user has access via a per-list share (i.e. they're not in
 * the parent board's editors/viewers, only the list's). Mirrors
 * useResolvedBoard's pattern but for the per-list-sharing path.
 *
 * Returns:
 *   { list, ownerUid, boardId, loading, error }
 *
 *   - list: the resolved list doc with id field added, or null
 *   - ownerUid: the owner of the parent board (= path segment users/{X})
 *   - boardId: the parent board's id (for navigation back, breadcrumbs, etc.)
 *   - loading: true until first resolution attempt completes
 *   - error: 'no-access' | 'deleted' | <string> | null
 *
 * Why a dedicated hook (vs reusing useResolvedBoard): boards live at
 * users/{uid}/boards/{boardId}, two segments below users. Lists live
 * at users/{uid}/boards/{boardId}/lists/{listId}, four segments
 * below — the path-walking math differs. Plus the sharedWithMe
 * pointer doc has different fields (refBoardId carries the parent
 * for navigation, kind='list' distinguishes from board entries).
 *
 * Indexes required (firestore.indexes.json):
 *   collectionGroup lists, fields [editors] and [viewers] with
 *   CONTAINS arrayConfig at COLLECTION_GROUP scope.
 *
 * Rules required (firestore.rules):
 *   match /{path=**}/lists/{listId} { allow read: if request.auth.uid
 *   in resource.data.editors|viewers } — same shape as the boards
 *   collectionGroup rule. Without this the LIST query fails statically.
 */
export function useResolvedList({ listId, currentUid }) {
  const [state, setState] = useState({
    list: null,
    ownerUid: null,
    boardId: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!listId || !currentUid) {
      setState({ list: null, ownerUid: null, boardId: null, loading: false, error: null });
      return undefined;
    }

    let cancelled = false;
    let unsubLive = null;

    async function resolve() {
      setState(prev => ({ ...prev, loading: true, error: null }));
      try {
        // Two queries in parallel: lists where current user is in
        // editors, and lists where in viewers. Merge the results
        // and pick the one whose doc id matches our listId.
        const editorsQ = query(
          collectionGroup(db, 'lists'),
          where('editors', 'array-contains', currentUid),
        );
        const viewersQ = query(
          collectionGroup(db, 'lists'),
          where('viewers', 'array-contains', currentUid),
        );

        const tryFindMatch = async () => {
          const [eSnap, vSnap] = await Promise.all([
            getDocs(editorsQ),
            getDocs(viewersQ),
          ]);
          for (const snap of [eSnap, vSnap]) {
            for (const d of snap.docs) {
              if (d.id === listId) return d;
            }
          }
          return null;
        };

        // Own-list fallback: when the current user OWNS the list,
        // they're not in editors/viewers (those arrays are for
        // collaborators). We walk the user's own boards looking for
        // a list with this id. This matches the case where someone
        // ctrl/cmd-clicks an own-list row in the sidebar to open it
        // in a new tab — sidebar rows now use /list/:id href for
        // both own and shared lists. Ran in parallel with the
        // editors/viewers query so neither blocks the other.
        const tryFindOwnList = async () => {
          const boardsSnap = await getDocs(collection(db, 'users', currentUid, 'boards'));
          for (const boardDoc of boardsSnap.docs) {
            const listSnap = await getDoc(
              doc(db, 'users', currentUid, 'boards', boardDoc.id, 'lists', listId),
            );
            if (listSnap.exists()) return listSnap;
          }
          return null;
        };

        let [match, ownMatch] = await Promise.all([
          tryFindMatch(),
          tryFindOwnList(),
        ]);
        if (cancelled) return;

        // Retry the SHARED-list path once after a brief wait if the
        // first query came back empty. Handles the Firestore
        // index-propagation race when the user JUST got added to the
        // list's editors (e.g. immediately after acceptShareInvite
        // redirects them here). arrayUnion writes can take ~1s to
        // propagate to the collectionGroup index; without a retry
        // the user lands on "list not found" until they manually
        // refresh. Own-list path doesn't need this — a freshly-
        // created own list is in the user's own subcollection
        // synchronously after the doc commits.
        if (!match && !ownMatch) {
          await new Promise(r => setTimeout(r, 1500));
          if (cancelled) return;
          match = await tryFindMatch();
          if (cancelled) return;
        }

        const resolved = match || ownMatch;
        if (!resolved) {
          setState({ list: null, ownerUid: null, boardId: null, loading: false, error: 'no-access' });
          return;
        }

        // Path walk: resolved.ref.path is users/{ownerUid}/boards/{boardId}/lists/{listId}.
        // resolved.ref.parent → lists collection
        // resolved.ref.parent.parent → board doc {boardId}
        // resolved.ref.parent.parent.parent → boards collection
        // resolved.ref.parent.parent.parent.parent → user doc {ownerUid}
        const boardId = resolved.ref.parent.parent.id;
        const ownerUid = resolved.ref.parent.parent.parent.parent.id;
        const data = { id: resolved.id, ...resolved.data() };
        const isOwnList = ownerUid === currentUid;

        setState({ list: data, ownerUid, boardId, loading: false, error: null });

        // Lazy sharedWithMe pointer write. Same idea as
        // useResolvedBoard: the act of opening a per-list share URL
        // adds it to the user's "shared with me" sidebar entry on
        // first resolve, no Cloud Function fan-out needed. We key
        // the doc by listId (boards key by boardId in their path),
        // so a board-share and a list-share to the same target
        // can coexist without overwriting each other.
        // Skip this for own lists — they don't belong in the
        // user's "shared with me" surface.
        if (!isOwnList) {
          try {
            await setDoc(
              doc(db, 'users', currentUid, 'sharedWithMe', listId),
              {
                kind: 'list',
                refOwnerUid: ownerUid,
                refId: listId,
                refBoardId: boardId,
                role: data.editors?.includes(currentUid) ? 'editor' : 'viewer',
                cachedTitle: data.title || '(untitled list)',
                addedAt: serverTimestamp(),
              },
              { merge: true },
            );
          } catch (err) {
            console.warn('[useResolvedList] sharedWithMe pointer write failed', err);
          }
        }

        // Live listener so renames + content edits flow through.
        unsubLive = onSnapshot(
          doc(db, 'users', ownerUid, 'boards', boardId, 'lists', listId),
          (snap) => {
            if (cancelled) return;
            if (!snap.exists()) {
              setState({ list: null, ownerUid: null, boardId: null, loading: false, error: 'deleted' });
              return;
            }
            setState(prev => ({
              ...prev,
              list: { id: snap.id, ...snap.data() },
              ownerUid,
              boardId,
              loading: false,
              error: null,
            }));
          },
          (err) => {
            console.warn('[useResolvedList] live snapshot error', err);
            // Owner removed us from the per-list ACL since resolve.
            setState({ list: null, ownerUid: null, boardId: null, loading: false, error: 'no-access' });
          }
        );
      } catch (err) {
        if (cancelled) return;
        console.error('[useResolvedList] resolve failed', err);
        setState({ list: null, ownerUid: null, boardId: null, loading: false, error: err.message });
      }
    }

    resolve();
    return () => {
      cancelled = true;
      if (unsubLive) unsubLive();
    };
  }, [listId, currentUid]);

  return state;
}
